// BestRx — City × Service authority page (white aesthetic, conversion-first).
import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowRight, Phone, MapPin, ShieldCheck } from "lucide-react";
import Footer from "@/components/landing/Footer";
import { useLeadModal } from "@/components/shared/LeadProvider";
import { CITIES, CITY_BY_SLUG, SERVICES } from "@/data/cities";
import { MEDICATIONS, ALL_MEDS } from "@/data/medications";
import { PRICING, fallbackPricing } from "@/data/medPricing";
import SEO, { breadcrumbSchema } from "@/components/shared/SEO";
import {
  PricingCards,
  NoInsuranceSection,
  HowItWorks4,
  RequestCallbackCTA,
  WhyBestRx,
  FAQAccordion,
  RelatedCluster,
  faqSchemaFrom,
  RED,
} from "@/components/shared/BestRxSections";

const PHONE = "844-703-1780";
const TEL = "tel:+18447031780";

function CityHeader({ onCta }) {
  return (
    <header className="absolute top-0 left-0 right-0 z-40 bg-white border-b border-black/10">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-4 flex items-center justify-between gap-4">
        <a href="/" className="font-heavy text-black leading-none" style={{ fontSize: "clamp(24px, 2.6vw, 36px)" }} data-testid="brand-logo">
          BEST<span style={{ color: RED }}>RX</span>
        </a>
        <div className="flex items-center gap-3">
          <a href={TEL} className="hidden sm:inline-flex items-center gap-2 font-extrabold text-[13px]" style={{ color: RED }} data-testid="header-phone">
            <Phone size={16} /> {PHONE}
          </a>
          <button
            onClick={onCta}
            data-testid="header-cta"
            className="inline-flex items-center gap-2 px-4 sm:px-6 py-3 font-extrabold uppercase text-[11.5px] sm:text-[13px] text-white tracking-[0.06em]"
            style={{ background: RED, boxShadow: "0 8px 20px rgba(225,29,42,0.30)" }}
          >
            Start Now <ArrowRight size={16} strokeWidth={2.6} />
          </button>
        </div>
      </div>
    </header>
  );
}

export default function CityServicePage() {
  const { city: citySlug, service: serviceSlug } = useParams();
  const navigate = useNavigate();
  const city = CITY_BY_SLUG[citySlug];
  const service = SERVICES[serviceSlug];
  const { open } = useLeadModal();
  const onCta = () => open({ source: `city_${citySlug}_${serviceSlug}` });

  useEffect(() => { window.scrollTo(0, 0); }, [citySlug, serviceSlug]);

  if (!city || !service) {
    return (
      <main className="bg-white min-h-screen text-black">
        <SEO title="Not Found" path={`/locations/${citySlug}/${serviceSlug}`} noindex />
        <CityHeader onCta={onCta} />
        <div className="pt-40 text-center px-6">
          <h1 className="font-heavy text-5xl">Not found</h1>
          <button onClick={() => navigate("/")} className="mt-8 px-6 py-3 font-extrabold text-white" style={{ background: RED }}>Back home →</button>
        </div>
        <Footer />
      </main>
    );
  }

  const featuredMed = service.meds?.[0] && MEDICATIONS[service.meds[0]];
  const startingPrice = featuredMed ? (PRICING[featuredMed.slug]?.[0]?.price || 199) : 199;
  const path = `/locations/${citySlug}/${serviceSlug}`;
  const description = `${service.h1(city.name)} — BestRx delivers ${service.name} programs to ${city.name}, TX residents. No insurance required, new customers start as low as $35 week 1*. Provider-reviewed, pharmacy-compounded, delivered to your door.`;
  const localMeds = (service.meds || []).map((s) => MEDICATIONS[s]).filter(Boolean);
  const faqItems = service.faq(city.name);

  return (
    <main className="bg-white text-black overflow-x-hidden min-h-screen" data-testid={`city-page-${citySlug}-${serviceSlug}`}>
      <SEO
        title={`${service.h1(city.name)} · From $${startingPrice}/mo`}
        path={path}
        description={description}
        schema={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: city.name, path: `/locations/${citySlug}` },
            { name: service.name, path },
          ]),
          faqSchemaFrom(faqItems),
          {
            "@context": "https://schema.org",
            "@type": "MedicalBusiness",
            name: `BestRx ${city.name}`,
            url: `https://bestrx.online${path}`,
            telephone: "+1-844-703-1780",
            areaServed: { "@type": "City", name: city.name, containedIn: { "@type": "State", name: "Texas" } },
            medicalSpecialty: service.name,
          },
        ]}
      />
      <CityHeader onCta={onCta} />

      {/* HERO */}
      <section className="relative pt-28 sm:pt-32 md:pt-36 pb-12 md:pb-16" data-testid="city-hero">
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="font-extrabold tracking-[0.18em] mb-3 text-[12px] md:text-[13px] inline-flex items-center gap-2" style={{ color: RED }}>
              <MapPin size={14} /> {city.name.toUpperCase()}, {city.state} · {service.name.toUpperCase()}
            </p>
            <h1 className="font-heavy leading-[0.92]" style={{ fontSize: "clamp(34px, 5.4vw, 86px)" }} data-testid="city-h1">
              {service.h1(city.name)}
            </h1>
            <p className="mt-5 text-[15px] md:text-[18px] text-black/75 leading-relaxed max-w-[640px]">
              {service.intro(city.name)}
            </p>

            {/* Price + CTAs */}
            <div
              className="mt-7 rounded-md border-2 p-6 inline-flex flex-col sm:flex-row items-start sm:items-center gap-5"
              style={{ borderColor: RED, boxShadow: "0 0 0 6px rgba(225,29,42,0.06)" }}
              data-testid="city-price-block"
            >
              <div>
                <p className="font-extrabold text-[11px] tracking-[0.18em]" style={{ color: RED }}>
                  STARTING AS LOW AS
                </p>
                <p className="font-heavy leading-none mt-1" style={{ fontSize: "clamp(38px, 4.2vw, 56px)", color: RED }}>
                  ${startingPrice}<span className="text-black/60 font-bold text-[16px] md:text-[20px] ml-1">/ month</span>
                </p>
                <p className="font-extrabold text-[12px] tracking-[0.12em] mt-2 text-black uppercase">
                  No Insurance Required · <span style={{ color: RED }}>$35 Week 1<sup>*</sup></span>
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:ml-auto">
                <button
                  onClick={onCta}
                  data-testid="city-cta-start"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 font-extrabold text-white tracking-[0.08em] uppercase text-[13px]"
                  style={{ background: RED, boxShadow: "0 12px 28px rgba(225,29,42,0.30)" }}
                >
                  Start Now <ArrowRight size={16} strokeWidth={2.6} />
                </button>
                <a
                  href={TEL}
                  data-testid="city-cta-call"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 font-extrabold tracking-[0.08em] uppercase text-[13px] border-2 border-black text-black"
                >
                  <Phone size={16} /> {PHONE}
                </a>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 flex items-center justify-center">
            <img
              src="/assets/hero-meds.png?v=2"
              alt={`BestRx ${service.name} in ${city.name}, TX`}
              className="w-full h-auto max-h-[480px] object-contain"
              draggable={false}
              data-testid="city-hero-image"
            />
          </div>
        </div>
      </section>

      {/* LOCAL INTRO + LANDMARKS */}
      <section className="bg-[#FAFAFA] py-12 md:py-16 border-y border-black/10" data-testid="local-intro">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-7">
            <h2 className="font-heavy leading-tight mb-4" style={{ fontSize: "clamp(24px, 3vw, 40px)" }}>
              SERVING <span style={{ color: RED }}>{city.name.toUpperCase()}</span> &amp; THE SURROUNDING AREAS
            </h2>
            <p className="text-[15px] text-black/75 leading-relaxed mb-4">
              From {city.landmarks?.slice(0, 3).join(", ")} to the rest of {city.note}, BestRx serves every neighborhood in {city.name}. Whether you're near {city.landmarks?.[3] || "the heart of the city"} or commuting in from {city.landmarks?.[4] || "the surrounding areas"}, our process is the same: take the assessment, get reviewed by a Texas-licensed clinician, and have your medication delivered.
            </p>
            <p className="text-[15px] text-black/75 leading-relaxed">
              {city.name} is {city.note}, with a population of {city.population}. Residents across {city.landmarks?.slice(-2).join(" and ")} choose BestRx because we offer the lowest prices on {service.name.toLowerCase()} medications in Texas, with no insurance hurdles and provider-reviewed prescriptions shipped straight to their door.
            </p>
          </div>
          <div className="md:col-span-5">
            <p className="font-extrabold tracking-[0.18em] text-[11.5px] mb-3" style={{ color: RED }}>NEIGHBORHOODS WE SERVE</p>
            <ul className="flex flex-wrap gap-2">
              {(city.landmarks || []).map((l) => (
                <li key={l} className="px-3 py-1.5 border border-black/15 text-[12px] font-bold text-black/80 rounded-sm">{l}</li>
              ))}
            </ul>
            {city.zips?.length > 0 && (
              <>
                <p className="font-extrabold tracking-[0.18em] text-[11.5px] mt-5 mb-3" style={{ color: RED }}>SERVING ZIP CODES</p>
                <p className="text-[12.5px] text-black/65 leading-relaxed">{city.zips.join(" · ")}</p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* MEDICATION OPTIONS GRID */}
      <section className="bg-white py-14 md:py-20" data-testid="city-meds-grid">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="mb-10">
            <p className="font-extrabold tracking-[0.18em] mb-2 text-[12px]" style={{ color: RED }}>
              {service.name.toUpperCase()} MEDICATIONS IN {city.name.toUpperCase()}
            </p>
            <h2 className="font-heavy leading-tight" style={{ fontSize: "clamp(28px, 3.6vw, 52px)" }}>
              YOUR <span style={{ color: RED }}>OPTIONS</span>.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {localMeds.map((med) => {
              const price = PRICING[med.slug]?.[0]?.price || 199;
              return (
                <Link
                  to={`/medications/${med.slug}`}
                  key={med.slug}
                  className="rounded-md border bg-white p-6 hover:border-red-600 transition-colors"
                  style={{ borderColor: "rgba(0,0,0,0.1)", boxShadow: "0 6px 16px rgba(0,0,0,0.04)" }}
                  data-testid={`city-med-${med.slug}`}
                >
                  <p className="font-extrabold text-[11px] tracking-[0.16em] mb-1.5" style={{ color: RED }}>{med.type?.toUpperCase()}</p>
                  <h3 className="font-heavy text-[20px] md:text-[22px] tracking-tight mb-3">{med.name}</h3>
                  <p className="text-[13.5px] text-black/65 leading-snug mb-5 line-clamp-2">{med.tagline}</p>
                  <div className="flex items-end justify-between">
                    <div>
                      <p className="text-[10.5px] font-extrabold tracking-[0.14em] text-black/55">FROM</p>
                      <p className="font-heavy leading-none" style={{ color: RED, fontSize: "32px" }}>${price}<span className="text-black/55 font-bold text-[14px] ml-1">/ mo</span></p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 font-extrabold text-[12px] tracking-[0.08em] uppercase" style={{ color: RED }}>
                      View <ArrowRight size={14} strokeWidth={2.6} />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRICING for featured med */}
      {featuredMed && (
        <PricingCards slug={featuredMed.slug} packages={PRICING[featuredMed.slug] || fallbackPricing(featuredMed.slug, 199)} />
      )}

      {/* NO INSURANCE */}
      <NoInsuranceSection />

      {/* HOW IT WORKS */}
      <HowItWorks4 />

      {/* WHY BESTRX */}
      <WhyBestRx />

      {/* REQUEST A CALLBACK */}
      <RequestCallbackCTA source={`city_${citySlug}_${serviceSlug}_callback`} />

      {/* FAQ */}
      <FAQAccordion items={faqItems} title={`${city.name} ${service.name} — FAQ`} />

      {/* RELATED CLUSTER */}
      <RelatedCluster medications={ALL_MEDS} cities={CITIES} currentCity={citySlug} />

      <Footer />
    </main>
  );
}
